import { CommonModule} from "@angular/common";
import {
  AfterViewInit,
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  linkedSignal,
  OnDestroy,
  signal,
  ViewChild,
} from "@angular/core";
import Highcharts from "highcharts";

import dashboardData from "../../../json/qca-dashboard.mock.json";
import qcaData from "../../../json/qca-management.mock.json";
import pssData from "../../../json/pss-management.mock.json";
import outageData from "../../../json/outage-management.mock.json";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { DsmBlockService } from "../../core/services/dsm-block.service";

type DashboardData = typeof dashboardData;
type ScheduleRow = DashboardData["scheduleAvcRows"][number];
type RevisionRow = DashboardData["revisionRows"][number];
type AlertRow = DashboardData["alerts"][number];
type UnitRow = DashboardData["unitScheduleRows"][number];

interface QcaOption {
  code: string;
  name: string;
}
interface PssOption {
  code: string;
  name: string;
  qcaCode?: string;
}

@Component({
  selector: "app-qca-dashboard",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./qca-dashboard.component.html",
  styleUrl: "./qca-dashboard.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QcaDashboardComponent implements AfterViewInit, OnDestroy {
  private readonly dsmBlockService = inject(DsmBlockService);
  private readonly route = inject(ActivatedRoute);
  private qcaQueryParamSubscription?: Subscription;

  @ViewChild("injectionChart")
  private injectionChartElement?: ElementRef<HTMLElement>;
  @ViewChild("injectionPanel")
  private injectionPanelElement?: ElementRef<HTMLElement>;
  @ViewChild("paymentChart")
  private paymentChartElement?: ElementRef<HTMLElement>;

  readonly source = dashboardData as DashboardData;
  private injectionChart?: Highcharts.Chart;
  private paymentChart?: Highcharts.Chart;
  private readonly onInjectionFullscreenChange = (): void => {
    this.refreshInjectionChart();
    requestAnimationFrame(() => this.injectionChart?.reflow());
  };

  readonly selectedQca = signal(
    this.route.snapshot.queryParamMap.get("qcaCode") ??
      this.route.snapshot.queryParamMap.get("qca") ??
      "All",
  );
  readonly selectedPss = signal(
    this.route.snapshot.queryParamMap.get("pss") ?? "All",
  );
  readonly selectedDate = signal(this.source.defaultDate);
  readonly selectedTimeBlock = linkedSignal(() => this.dsmBlockService.currentBlockWindow());
  readonly timeBlockOptions = this.dsmBlockService.blockWindows;
  readonly selectedView = signal(this.source.defaultView);
  readonly searchTerm = signal("");
  readonly activeRevisionTab = signal<"all" | "schedule" | "avc">("all");
  readonly injectionResolution = signal<"hour" | "block">("hour");
  readonly visibleRevisionCount = signal(10);
  readonly qcas = signal<QcaOption[]>([]);
  readonly psses = signal<PssOption[]>([]);
  readonly outages = signal<readonly unknown[]>(outageData.outages);


  readonly qcaOptions = computed(() => [
    { code: "All", name: "All QCAs" },
    ...this.qcas(),
  ]);
  readonly pssOptions = computed(() => {
    const qca = this.selectedQca();
    const values =
      qca === "All"
        ? this.psses()
        : this.psses().filter((pss) => pss.qcaCode === qca);
    return [{ code: "All", name: "All PSS" }, ...values];
  });

  readonly scheduleRows = computed(() => {
    const qca = this.selectedQca();
    const pss = this.selectedPss();
    const query = this.searchTerm().trim().toLowerCase();
    return this.source.scheduleAvcRows.filter((row) => {
      const matchesFilter =
        (qca === "All" || row.qcaCode === qca) &&
        (pss === "All" || row.pssCode === pss);
      const text =
        `${row.qcaCode} ${row.pssCode} ${this.pssName(row.pssCode)}`.toLowerCase();
      return matchesFilter && (!query || text.includes(query));
    });
  });

  readonly revisionRows = computed(() => {
    const qca = this.selectedQca();
    const pss = this.selectedPss();
    const query = this.searchTerm().trim().toLowerCase();
    return this.source.revisionRows.filter((row) => {
      const matchesFilter =
        (qca === "All" || this.pssQca(row.pssCode) === qca) &&
        (pss === "All" || row.pssCode === pss);
      const text =
        `${row.pssCode} ${this.pssName(row.pssCode)} ${row.revisionType}`.toLowerCase();
      return matchesFilter && (!query || text.includes(query));
    });
  });
  readonly visibleRevisionRows = computed(() =>
    this.revisionRows().slice(0, this.visibleRevisionCount()),
  );

  readonly unitRows = computed(() => {
    const pss = this.selectedPss();
    const query = this.searchTerm().trim().toLowerCase();
    return this.source.unitScheduleRows.filter((row) => {
      const text =
        `${row.pssCode} ${row.companyName} ${row.unitName}`.toLowerCase();
      return (
        (pss === "All" || row.pssCode === pss) &&
        (!query || text.includes(query))
      );
    });
  });

  readonly currentInjection = computed(() => {
    const selectedBlockStart = this.selectedTimeBlock().split(' - ')[0];
    const selected = this.source.injectionSeries.find(
      (item) => item.time === selectedBlockStart,
    );
    return selected ?? this.source.injectionSeries[0];
  });

  readonly kpis = computed(() => {
    const rows = this.scheduleRows();
    const schedule = rows.reduce((sum, row) => sum + row.requiredScheduleMw, 0);
    const actual = rows.reduce((sum, row) => sum + row.actualInjectionMw, 0);
    const outageCount = this.outages().filter(
      (outage: any) => outage.status === "Active" || outage.status === "active",
    ).length;
    return [
      {
        label: "Total Schedule",
        value: this.formatNumber(schedule),
        unit: "MW",
        caption: "Required schedule",
        tone: "blue",
        icon: "▣",
      },
      {
        label: "Actual Injection",
        value: this.formatNumber(actual),
        unit: "MW",
        caption: "Submitted actual",
        tone: "green",
        icon: "⌁",
      },
      {
        label: "Deviation",
        value: this.formatNumber(actual - schedule),
        unit: "MW",
        caption: `${schedule ? this.formatNumber(((actual - schedule) / schedule) * 100) : "0"}% of schedule`,
        tone: "orange",
        icon: "⌁",
      },
      {
        label: "DSM Exposure",
        value: this.formatNumber(this.source.metrics.dsmExposureRs),
        unit: "₹",
        caption: "Estimated exposure",
        tone: "purple",
        icon: "₹",
      },
      {
        label: "Generator Outages",
        value: this.formatNumber(outageCount),
        unit: "",
        caption: "Units offline",
        tone: "red",
        icon: "△",
      },
      {
        label: "Real-time Visibility",
        value: `${this.source.metrics.realTimeVisibilityPercent}%`,
        unit: "",
        caption: "Good",
        tone: "teal",
        icon: "▥",
      },
      {
        label: "Curtailments",
        value: this.formatNumber(this.source.metrics.curtailments),
        unit: "",
        caption: `Total ${this.source.metrics.curtailmentMw} MW`,
        tone: "amber",
        icon: "⌘",
      },
    ];
  });
  readonly paymentSummaryTotal = computed(() =>
    this.source.paymentSummaryYtd.reduce(
      (total, payment) => total + payment.amount,
      0,
    ),
  );

  constructor(private readonly router: Router) {
    const qcaRows = (qcaData as any).rows ?? (qcaData as any).qcas ?? [];
    const pssRows = (pssData as any).rows ?? (pssData as any).pss ?? [];
    this.qcas.set(
      qcaRows.map((row: any) => ({ code: row.code, name: row.name })),
    );
    this.psses.set(
      pssRows.map((row: any) => ({
        code: row.code,
        name: row.name,
        qcaCode: row.qcaCode,
      })),
    );
    this.qcaQueryParamSubscription = this.route.queryParamMap.subscribe(
      (params) => this.selectQcaFromRoute(
        params.get("qcaCode") ?? params.get("qca"),
        params.get("pss"),
      ),
    );
    afterNextRender(() => {
      const params = new URLSearchParams(window.location.search);
      this.selectQcaFromRoute(
        params.get("qcaCode") ?? params.get("qca"),
        params.get("pss"),
      );
    });
    // Keep the actual-injection line bounded to the current DSM block and
    // extend it automatically as the block advances (DsmBlockService).
    effect(() => {
      this.dsmBlockService.currentBlock();
      this.refreshInjectionChart();
    });
  }

  ngAfterViewInit(): void {
    this.createInjectionChart();
    this.createPaymentChart();
    document.addEventListener("fullscreenchange", this.onInjectionFullscreenChange);
  }

  ngOnDestroy(): void {
    this.qcaQueryParamSubscription?.unsubscribe();
    this.injectionChart?.destroy();
    this.paymentChart?.destroy();
    document.removeEventListener("fullscreenchange", this.onInjectionFullscreenChange);
  }

  applyFilters(): void {
    this.searchTerm.set(this.searchTerm().trim());
    this.refreshInjectionChart();
  }

  resetFilters(): void {
    this.selectedQca.set("All");
    this.selectedPss.set("All");
    this.selectedDate.set(this.source.defaultDate);
    this.selectedTimeBlock.set(this.dsmBlockService.currentBlockWindow());
    this.selectedView.set(this.source.defaultView);
    this.searchTerm.set("");
    this.refreshInjectionChart();
  }

  selectPss(pssCode: string): void {
    this.selectedPss.set(pssCode);
    // const qcaCode = this.pssQca(pssCode);
    // if (qcaCode) this.selectedQca.set(qcaCode);
    this.router.navigate(['/pss-dashboard', pssCode]);
  }

  onQcaChange(qcaCode: string): void {
    this.selectedQca.set(qcaCode);
    this.selectedPss.set("All");
  }

  onPssChange(pssCode: string): void {
    this.selectedPss.set(pssCode);
    if (pssCode === "All") {
      return;
    }

    const qcaCode = this.pssQca(pssCode);
    if (qcaCode) {
      this.selectedQca.set(qcaCode);
    }
  }

  private selectQcaFromRoute(
    qcaCode: string | null,
    pssCode: string | null,
  ): void {
    const selectedPss = this.psses().find((pss) => pss.code === pssCode?.trim());
    if (selectedPss) {
      this.selectedQca.set(selectedPss.qcaCode ?? "All");
      this.selectedPss.set(selectedPss.code);
      return;
    }

    const normalizedCode = qcaCode?.trim();
    if (!normalizedCode || !this.qcas().some((qca) => qca.code === normalizedCode)) {
      return;
    }

    this.selectedQca.set(normalizedCode);
    this.selectedPss.set("All");
  }

  onTimeBlockChange(value: string): void {
    this.selectedTimeBlock.set(value);
  }

  loadMoreRevisionRows(event: Event): void {
    const container = event.target as HTMLElement;
    const reachedBottom =
      container.scrollTop + container.clientHeight >= container.scrollHeight - 4;

    if (reachedBottom && this.visibleRevisionCount() < this.revisionRows().length) {
      this.visibleRevisionCount.update((count) => count + 10);
    }
  }

  pssName(pssCode: string): string {
    return this.psses().find((pss) => pss.code === pssCode)?.name ?? pssCode;
  }

  qcaName(qcaCode: string): string {
    return this.qcas().find((qca) => qca.code === qcaCode)?.name ?? qcaCode;
  }

  pssQca(pssCode: string): string | undefined {
    return this.psses().find((pss) => pss.code === pssCode)?.qcaCode;
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(
      value,
    );
  }

  formatMw(value: number): string {
    return value.toFixed(2);
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  }

  paymentShare(amount: number): string {
    const total = this.paymentSummaryTotal();
    return total ? ((amount / total) * 100).toFixed(1) : "0.0";
  }

  statusClass(status: string): string {
    return status.toLowerCase().replace(/\s+/g, "-");
  }

  alertClass(alert: AlertRow): string {
    return alert.level;
  }

  alertTitle(title: string): string {
    return title.replace(/([A-Za-z]+)-(\d+)/g, "$1‑$2");
  }

  setInjectionResolution(resolution: "hour" | "block"): void {
    if (this.injectionResolution() === resolution) return;
    this.injectionResolution.set(resolution);
    this.refreshInjectionChart();
  }

  toggleInjectionFullscreen(): void {
    const panel = this.injectionPanelElement?.nativeElement;
    if (!panel) return;

    if (document.fullscreenElement === panel) {
      void document.exitFullscreen();
      return;
    }

    void panel.requestFullscreen?.();
  }

  private createInjectionChart(): void {
    if (!this.injectionChartElement) return;

    this.injectionChart = Highcharts.chart(
      this.injectionChartElement.nativeElement,
      {
        chart: {
          type: "spline",
          backgroundColor: "transparent",
          animation: false,
          margin: [18, 12, 68, 64],
          zooming: { type: "x" },
          panning: { enabled: true, type: "x" },
          panKey: "shift",
          style: { fontFamily: "var(--app-font-sans)" },
        },
        accessibility: { enabled: false },
        credits: { enabled: false },
        title: { text: undefined },
        xAxis: this.injectionXAxisOptions(),
        yAxis: {
          min: 0,
          title: {
            text: "MW",
            margin: 12,
            style: { color: "#475569", fontSize: "10px" },
          },
          gridLineColor: "#e9eef4",
          labels: {
            format: "{value:,.0f}",
            style: { color: "#64748b", fontSize: "9px" },
          },
        },
        tooltip: {
          shared: true,
          valueSuffix: " MW",
          backgroundColor: "#ffffff",
          borderColor: "#dce4ee",
          borderRadius: 4,
          shadow: false,
          style: { color: "#000000", fontSize: "11px" },
        },
        legend: {
          enabled: true,
          align: "center",
          verticalAlign: "bottom",
          layout: "horizontal",
        margin: 0,
        y: 16,
          itemDistance: 12,
          itemStyle: { color: "#000000", fontSize: "9px", fontWeight: "500" },
          itemHiddenStyle: { color: "#94a3b8" },
        },
        plotOptions: {
          series: {
            marker: { enabled: true, radius: 2 },
            states: { hover: { lineWidthPlus: 1 } },
          },
        },
        series: this.injectionSeries(),
      },
    );
  }

  private createPaymentChart(): void {
    if (!this.paymentChartElement) return;

    this.paymentChart = Highcharts.chart(
      this.paymentChartElement.nativeElement,
      {
        chart: {
          type: "pie",
          backgroundColor: "transparent",
          animation: false,
          spacing: [0, 0, 0, 0],
          style: { fontFamily: "var(--app-font-sans)" },
        },
        accessibility: { enabled: false },
        credits: { enabled: false },
        title: { text: undefined },
        tooltip: {
          pointFormat: "<b>{point.percentage:.1f}%</b> · ₹ {point.y:,.0f}",
          backgroundColor: "#ffffff",
          borderColor: "#dce4ee",
          borderRadius: 4,
          shadow: false,
          style: { color: "#000000", fontSize: "11px" },
        },
        legend: { enabled: false },
        plotOptions: {
          pie: {
            innerSize: "62%",
            borderColor: "#ffffff",
            borderWidth: 2,
            dataLabels: { enabled: false },
          },
        },
        series: [
          {
            type: "pie",
            name: "Amount paid",
            data: this.source.paymentSummaryYtd.map((payment) => ({
              name: payment.label,
              y: payment.amount,
              color: payment.color,
            })),
          },
        ],
      },
    );
  }

  private refreshInjectionChart(): void {
    if (!this.injectionChart) return;
    const series = this.injectionSeries();
    this.injectionChart.xAxis[0].update(this.injectionXAxisOptions(), false);
    this.injectionChart.series.forEach((chartSeries, index) => {
      chartSeries.setData(
        (series[index] as Highcharts.SeriesSplineOptions).data ?? [],
        false,
      );
    });
    this.injectionChart.redraw();
  }

  private injectionSeries(): Highcharts.SeriesOptionsType[] {
    const points = this.injectionPoints();
    return [
      {
        type: "spline",
        name: "Schedule",
        color: "#2563eb",
        data: points.map((point) => point.scheduleMw),
      },
      {
        type: "spline",
        name: "Revised schedule",
        color: "#16a365",
        dashStyle: "ShortDash",
        data: points.map(
          (point) => point.revisedScheduleMw,
        ),
      },
      {
        type: "spline",
        name: "Actual injection",
        color: "#ef4444",
        data: this.actualInjectionValues(),
      },
    ];
  }

  /**
   * Actual injection is reported only for completed DSM blocks. At 14:56 IST,
   * block 60 (14:45-15:00) is in progress, so block 59 (14:30-14:45) is
   * the latest actual value. The block view retains all 96 categories and
   * nulls the values from the current block onward.
   */
  private actualInjectionValues(): Array<number | null> {
    const lastCompletedBlockIndex = this.dsmBlockService.currentBlockNumber() - 2;
    return this.injectionPoints().map((point, index) =>
      index <= lastCompletedBlockIndex ? point.actualInjectionMw : null,
    );
  }

  private injectionPoints() {
    return this.injectionResolution() === "hour"
      ? this.source.injectionSeries
      : this.source.injectionBlockSeries;
  }

  private injectionXAxisOptions(): Highcharts.XAxisOptions {
    const categories = this.injectionPoints().map((point) => point.time);
    const isHourResolution = this.injectionResolution() === "hour";
    const isFullscreen = document.fullscreenElement === this.injectionPanelElement?.nativeElement;
    const tickPositions = isHourResolution
      ? Array.from({ length: 24 }, (_, index) => index * 4)
      : isFullscreen
        ? Array.from({ length: 96 }, (_, index) => index)
        : Array.from({ length: 24 }, (_, index) => index * 4);

    return {
      categories,
      min: 0,
      max: categories.length - 1,
      crosshair: { width: 1, color: "#9eb7df", dashStyle: "Dash" },
      lineColor: "#dce4ee",
      tickLength: 0,
      tickPositions,
      labels: {
        autoRotation: [],
        rotation: isHourResolution ? -45 : isFullscreen ? 1 : 0,
        step: 1,
        allowOverlap: true,
        style: { color: "#64748b", fontSize: isFullscreen && !isHourResolution ? "8px" : "9px" },
        formatter: function () {
          const position = this.pos as number;
          return isHourResolution
            ? categories[position] ?? ""
            : `${position + 1}`;
        },
      },
    };
  }
}
